import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User2Icon, ChevronDown, LogOut, FileArchive, Users, Building, History, MessageSquare, PlusCircle } from 'lucide-react';

const PageHeader = ({ 
  currentUser, 
  tabValue, 
  setTabValue,
  userDropdownOpen, 
  setUserDropdownOpen,
  mobileMenuOpen,
  setMobileMenuOpen,
  handleLogout
}) => {
  // Component for Tabs with animated icons
  const StaffTab = ({ icon: Icon, label, isActive, onClick }) => (
    <motion.button
      onClick={onClick}
      className={`flex items-center gap-3 px-5 py-3 rounded-lg transition-all duration-300 ${
        isActive 
          ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg shadow-indigo-600/20' 
          : 'text-white hover:bg-indigo-50 hover:text-indigo-900'
      }`}
      whileHover={!isActive ? { scale: 1.03 } : {}}
      whileTap={{ scale: 0.97 }}
    >
      <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-white'}`} />
      <span className="font-medium">{label}</span>
    </motion.button>
  );

  return (
    <div className="relative z-10">
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-900 via-blue-800 to-indigo-900 overflow-hidden">
        {/* Animated Orbs */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-br from-blue-400/10 to-indigo-400/10 blur-xl"
            style={{
              width: `${Math.random() * 100 + 50}px`,
              height: `${Math.random() * 100 + 50}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              x: [0, Math.random() * 40 - 20, 0],
              y: [0, Math.random() * 40 - 20, 0],
              scale: [1, Math.random() * 0.3 + 1, 1],
            }}
            transition={{
              duration: Math.random() * 10 + 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
        
        {/* Mesh Grid Overlay */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '20px 20px'
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          {/* Left side - Title */}
          <div>
            <motion.h1 
              className="text-2xl md:text-3xl font-bold text-white"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Panel de Administración
              <span className="ml-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-200">
                Staff
              </span>
            </motion.h1>
          </div>

          {/* Right side - User profile */}
          <div className="relative">
            <motion.div 
              className="hidden md:flex items-center"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <motion.button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 text-white/80 hover:text-white rounded-lg
                          transition-all duration-300 hover:bg-white/10"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center">
                  <User2Icon className="w-4 h-4 text-white" />
                </div>
                <span className="font-medium">{currentUser?.nombre || 'Staff'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300
                                      ${userDropdownOpen ? 'rotate-180' : ''}`} />
              </motion.button>

              <AnimatePresence>
                {userDropdownOpen && (
                  <motion.div
                    className="absolute right-0 mt-10 w-48 py-2 bg-white rounded-xl
                             border border-gray-100 shadow-xl overflow-hidden z-30"
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                    >
                      <LogOut className="w-4 h-4 mr-3 text-indigo-600" />
                      Cerrar Sesión
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Mobile menu */}
            <div className="md:hidden">
              <motion.button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-white/80 hover:text-white rounded-lg
                         transition-colors duration-300 hover:bg-white/10"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {mobileMenuOpen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                )}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Navigation tabs */}
        <motion.div 
          className="hidden md:flex mt-6 space-x-3 flex-wrap"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <StaffTab 
            icon={FileArchive} 
            label="Reportes" 
            isActive={tabValue === 0}
            onClick={() => setTabValue(0)}
          />
          <StaffTab 
            icon={Users} 
            label="Usuarios" 
            isActive={tabValue === 1}
            onClick={() => setTabValue(1)}
          />
          <StaffTab 
            icon={Building} 
            label="Propiedades" 
            isActive={tabValue === 2}
            onClick={() => setTabValue(2)}
          />
          <StaffTab 
            icon={History} 
            label="Historial" 
            isActive={tabValue === 3}
            onClick={() => setTabValue(3)}
          />
          <StaffTab 
            icon={MessageSquare} 
            label="Contactos" 
            isActive={tabValue === 4}
            onClick={() => setTabValue(4)}
          />
          <StaffTab 
            icon={PlusCircle} 
            label="Registrar Constructora" 
            isActive={tabValue === 5}
            onClick={() => setTabValue(5)}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default PageHeader;